import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMe, useLogout, MVersionsZeile } from "@vimaana/ui";
import { useMailKonto, useMailTrennen, useMailVerbinden } from "../hooks/useMail";
import SignaturenPflege from "../components/einstellungen/SignaturenPflege";
import StammdatenSektionen from "../components/einstellungen/StammdatenSektionen";
import KalenderAboSektion from "../components/einstellungen/KalenderAboSektion";
import BewertungEinstellung from "../components/einstellungen/BewertungEinstellung";

export default function EinstellungenPage() {
  const me = useMe("kontor") as { data?: { full_name?: string; email?: string } };
  const logout = useLogout("kontor") as { mutate: () => void };
  return (
    <div style={wrap}>
      <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800 }}>Einstellungen</h1>
      <div style={{ color: "var(--vg-text-2)", marginTop: 4, fontSize: 13, marginBottom: 22 }}>Konto &amp; App</div>

      <section style={card}>
        <div style={h2}>Konto</div>
        <div style={{ fontWeight: 700 }}>{me?.data?.full_name || "—"}</div>
        <div style={{ color: "var(--vg-text-2)", fontSize: 13 }}>{me?.data?.email || ""}</div>
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <Link to="/change-password" style={btnGhost}>Passwort ändern</Link>
          <button onClick={() => logout.mutate()} style={btnGhost}>Abmelden</button>
        </div>
      </section>

      <KalenderAboSektion />

      <PostfachSektion />

      <BewertungEinstellung />

      <StammdatenSektionen />

      {/* Claras Protokoll (früher „KI-Spur", Etappe 3, 28.09.2026): was Clara vorschlägt und was daraus wurde. */}
      <section style={card}>
        <div style={h2}>Claras Protokoll</div>
        <div style={{ color: "var(--vg-text-2)", fontSize: 13, marginBottom: 12 }}>
          Jeder Vorschlag von Clara — Mailentwürfe und gelesene Belege: woraus er entstand, womit sie gerechnet hat, und ob er übernommen, geändert oder verworfen wurde.
        </div>
        <Link to="/einstellungen/ki-spur" style={btnGhost}>Protokoll ansehen</Link>
      </section>

      <section style={card}>
        <div style={h2}>App</div>
        <div style={{ color: "var(--vg-text-2)", fontSize: 13 }}>Kontor · Web-Version · kontor.vimaanagarten.de</div>
      </section>
    </div>
  );
}

/* ══ Eigenes Postfach ════════════════════════════════════════════════════ */
//
// Hier wird das Postfach nur VERBUNDEN und GETRENNT. Belege holen ist Arbeit an
// den Finanzen und sitzt deshalb auf der Finanzen-Seite im Belege-Reiter — ein
// Einstellungsdialog ist der falsche Ort für einen wiederkehrenden Arbeitsschritt.

/** Rückmeldungen, mit denen der OAuth-Callback zurückleitet. */
const RUECKMELDUNG: Record<string, { art: "gut" | "warn"; text: string }> = {
  verbunden: { art: "gut", text: "Postfach verbunden." },
  abgebrochen: { art: "warn", text: "Die Verbindung wurde bei Google abgebrochen." },
  fehler: { art: "warn", text: "Google hat die Anmeldung abgelehnt. Bitte erneut versuchen." },
  "ohne-refresh": {
    art: "warn",
    text: "Google hat keine dauerhafte Berechtigung erteilt. Bitte erneut verbinden und die Freigabe bestätigen.",
  },
};

function PostfachSektion() {
  const [params, setParams] = useSearchParams();
  const konto = useMailKonto();
  const verbinden = useMailVerbinden();
  const trennen = useMailTrennen();

  const [trennenArmed, setTrennenArmed] = useState(false);

  const rueckmeldung = RUECKMELDUNG[params.get("mail") || ""];
  useEffect(() => {
    // Die Rückmeldung einmal zeigen, dann aus der Adresszeile entfernen —
    // sonst steht sie nach jedem Neuladen wieder da.
    if (!rueckmeldung) return;
    konto.refetch();
    const t = setTimeout(() => {
      params.delete("mail");
      setParams(params, { replace: true });
    }, 6000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rueckmeldung]);

  const k = konto.data;
  const fehlermeldung = (e: unknown) => (e instanceof Error ? e.message : String(e));

  return (
    <section style={card}>
      <div style={h2}>Eigenes Postfach</div>

      {rueckmeldung && (
        <div style={rueckmeldung.art === "gut" ? bannerGut : bannerWarn}>{rueckmeldung.text}</div>
      )}

      {konto.isPending ? (
        <div style={{ color: "var(--vg-text-2)", fontSize: 14 }}>Wird geladen …</div>
      ) : konto.isError ? (
        <div style={bannerWarn}>{fehlermeldung(konto.error)}</div>
      ) : k?.verbunden ? (
        <>
          <p style={text}>
            Verbunden mit <strong>{k.email || "deinem Google-Konto"}</strong>. Angebote und Rechnungen
            gehen unter dieser Adresse raus, Belege werden aus diesem Postfach gelesen.
          </p>
          {k.letzter_sync_at && (
            <p style={{ ...text, color: "var(--vg-text-3)" }}>
              Letzter Beleg-Abruf: {new Date(k.letzter_sync_at).toLocaleString("de-DE")}
              {k.letzter_sync_status ? ` · ${k.letzter_sync_status}` : ""}
            </p>
          )}
          <p style={text}>
            Belege holst du unter{" "}
            <Link to="/finanzen?reiter=belege" style={{ color: "var(--vg-accent)" }}>
              Finanzen → Belege
            </Link>
            .
          </p>

          <SignaturenPflege />

          <div style={{ ...zeile, marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--vg-line)" }}>
            {!trennenArmed ? (
              <button style={btnGhost} onClick={() => setTrennenArmed(true)}>Verbindung lösen</button>
            ) : (
              <>
                <span style={{ ...text, margin: 0 }}>
                  Sicher? Danach werden keine Belege mehr geholt und der Versand läuft nicht mehr über dein Konto.
                </span>
                <button
                  style={btnGhost}
                  disabled={trennen.isPending}
                  onClick={() => trennen.mutate(undefined, { onSuccess: () => setTrennenArmed(false) })}
                >
                  {trennen.isPending ? "Löst …" : "Ja, lösen"}
                </button>
                <button style={btnGhost} onClick={() => setTrennenArmed(false)}>Abbrechen</button>
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <p style={text}>
            Verbinde dein eigenes E-Mail-Konto. Danach werden Belege aus <em>deinem</em> Postfach eingelesen,
            und Angebote und Rechnungen gehen unter <em>deiner</em> Adresse raus — nicht mehr über ein
            gemeinsames Sammelkonto.
          </p>
          <p style={{ ...text, color: "var(--vg-text-3)" }}>
            Kontor bekommt dabei nur Zugriff auf Lesen und Senden von Mails. Du kannst die Freigabe hier oder
            in deinem Google-Konto jederzeit wieder entziehen.
          </p>
          <button
            style={{ ...btnPrimary, marginTop: 12, opacity: verbinden.isPending ? 0.6 : 1 }}
            disabled={verbinden.isPending}
            onClick={() => verbinden.mutate()}
          >
            {verbinden.isPending ? "Öffnet Google …" : "Postfach verbinden"}
          </button>
          {verbinden.isError && (
            <div style={bannerWarn}>
              {fehlermeldung(verbinden.error)}
              {k?.weiterleitung && (
                <div style={{ marginTop: 6, color: "var(--vg-text-2)" }}>
                  In der Google Cloud Console muss diese Weiterleitungsadresse freigegeben sein:
                  <code style={code}>{k.weiterleitung}</code>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <MVersionsZeile name="Kontor" />
    </section>
  );
}

const wrap: CSSProperties = { maxWidth: 780, margin: "0 auto", padding: "22px 22px 60px" };
const card: CSSProperties = { background: "var(--vg-bg-2)", border: "1px solid var(--vg-line)", borderRadius: 12, padding: "16px 18px", marginBottom: 14 };
const h2: CSSProperties = { fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6, color: "var(--vg-text-3)", marginBottom: 10 };
const text: CSSProperties = { color: "var(--vg-text-2)", fontSize: 14, lineHeight: 1.5, margin: "0 0 8px" };
const zeile: CSSProperties = { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 };
const code: CSSProperties = { display: "block", marginTop: 4, padding: "6px 8px", borderRadius: 6, background: "var(--vg-bg-3)", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12 };
const bannerGut: CSSProperties = { margin: "10px 0", padding: "10px 12px", borderRadius: 9, border: "1px solid var(--vg-green-line)", background: "var(--vg-green-soft)", color: "var(--k-on-green-soft)", fontSize: 13.5 };
const bannerWarn: CSSProperties = { margin: "10px 0", padding: "10px 12px", borderRadius: 9, border: "1px solid var(--vg-amber-line)", background: "var(--vg-amber-soft)", color: "var(--k-on-amber-soft)", fontSize: 13.5 };
const btnGhost: CSSProperties = { padding: "8px 14px", borderRadius: 9, border: "1px solid var(--vg-line-2)", background: "var(--vg-bg-2)", color: "var(--vg-text)", fontSize: 14, cursor: "pointer", textDecoration: "none" };
const btnPrimary: CSSProperties = { padding: "8px 14px", borderRadius: 9, border: "none", background: "var(--vg-accent)", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" };
